/**
 * ArkUI.BackStack —— 把系统返回键接进浮层栈
 *
 * 问题：浏览器 / WebView 里，浮层（抽屉、弹窗）打开时按系统返回键 = 整页导航走人，
 * 浮层里未保存的东西一并丢掉。原生 app 的返回键是「关掉最上面那层」。
 *
 * 做法：浮层打开时 history.pushState 一个同 URL 的占位条目，返回键把它弹掉时
 * 我们收到 popstate，转成「关闭最上层浮层」而不是导航。
 *
 * ── 为什么必须由宿主显式 enable() ──
 * 同 URL 的 pushState + back 照样会触发 Blazor 的 LocationChanged（Blazor 的 popstate
 * 监听器不比较 URI，一律 NotifyLocationChanged）。而 Ark.Client/Routes.razor 对任何
 * LocationChanged 都会 CloseAllTransient()，POS 上按一次返回就会清空全部临时弹窗。
 * 所以默认不开：不调 enable() 的宿主（POS）行为与从前完全一致，一行代码都不受影响。
 *
 * ── 为什么只在手机宽度生效 ──
 * 桌面没有系统返回键，只有浏览器后退按钮；给每个弹窗塞一条历史记录会污染后退栈，
 * 而收益为零。768 与 Admin 全站的 md 断点同源。
 */

const STATE_KEY = '__arkBack';

let enabled = false;
let listenerBound = false;

/** 浮层栈（LIFO）：{ token, href, invoke } */
const stack = [];

/** 由 pop() 主动触发的那次 history.back()，其 popstate 不应再回调 .NET */
let selfPopToken = null;

/**
 * pop() 想弹掉但还没真弹的那条占位条目：{ token, href }。
 *
 * 为什么要拖一个宏任务再弹 —— 「关掉这一层、同时打开下一层」是这套导航模型里的常规动作
 * （手机 Sheet 点一项 → Sheet 关闭 + 弹窗打开），两件事落在同一批渲染里，
 * pop() 与 push() 的 JS 调用先后并不确定。若 pop() 立刻 history.back()：
 *   ① back() 是异步的，popstate 要等到下一轮才来；
 *   ② 这中间 push() 已经把新层压进 stack 并 pushState 了一条；
 *   ③ 等 popstate 到达时栈顶已经换成新层，onPopState 拿栈顶去比 selfPopToken 会不相等，
 *      于是把「刚打开的那一层」当成用户按了返回键，直接关掉。
 * 表现就是：点 Sheet 里的项，什么都没发生。
 *
 * 拖一拍之后，push() 有机会先到：一关一开对历史栈是净零，直接把这条占位条目让给新层复用。
 */
let pendingPop = null;

function isPhone() {
    try {
        return window.matchMedia('(max-width: 767px)').matches;
    } catch {
        return window.innerWidth < 768;
    }
}

function bind() {
    if (listenerBound) return;
    window.addEventListener('popstate', onPopState);
    listenerBound = true;
}

function onPopState() {
    // 自弹优先判，且必须判在动栈之前：
    // 这条 popstate 是 flushPop() 那次 history.back() 的回声，.NET 那边早就关过了。
    // 不拿栈顶去比 token —— 被弹掉的那条未必还是栈顶（其间可能有新层压进来），
    // 一比就会把新层当成「用户按了返回键」直接关掉。栈也不在这里动：pop() 已经摘过了。
    if (selfPopToken !== null) {
        selfPopToken = null;
        return;
    }

    if (stack.length === 0) return;

    const top = stack[stack.length - 1];

    // 真导航（不是我们那条占位条目被弹掉）：URL 变了。
    // 此时页面本身要换掉，浮层会随组件卸载消失，栈整个作废——
    // 不要在这里回调 .NET 关浮层，那会在一个正在被卸载的组件上派发事件。
    if (window.location.href !== top.href) {
        stack.length = 0;
        pendingPop = null;
        return;
    }

    stack.pop();

    // 真·用户按了返回键 → 关掉这一层
    try {
        top.invoke();
    } catch {
        // 组件已卸载等：吞掉，历史条目已消费
    }
}

/**
 * 浮层打开时调用。
 * @returns {boolean} 是否真的接管了（未 enable / 非手机宽度 → false，调用方无需关心）
 */
function push(token, dotNetRef, method) {
    if (!enabled || !token || !dotNetRef) return false;
    if (!isPhone()) return false;
    // 同一个 token 重复 push（参数回流导致的重入）只认第一次
    if (stack.some(e => e.token === token)) return false;

    bind();

    const entry = {
        token,
        href: window.location.href,
        invoke: () => dotNetRef.invokeMethodAsync(method)
    };

    // 有一条待弹的占位条目：一关一开净零，新层直接复用它，那次 back() 就此取消。
    // 这条路径不碰 history —— 条目本来就在，重复 pushState 会凭空多一格。
    if (pendingPop && pendingPop.href === entry.href) {
        pendingPop = null;
        stack.push(entry);
        return true;
    }

    stack.push(entry);

    try {
        history.pushState({ [STATE_KEY]: token }, '');
    } catch {
        // 极少数环境禁用 History API：回退成「不接管」，返回键仍是原来的行为
        stack.pop();
        return false;
    }
    return true;
}

/**
 * 浮层以任何非「系统返回键」的方式关闭时调用（点遮罩 / 关闭按钮 / 上层代码置 false / Dispose）。
 * 必须调用，否则那条占位历史条目会留下来，下一次按返回键看起来「没反应」。
 */
function pop(token) {
    if (!token) return;

    const idx = stack.findIndex(e => e.token === token);
    if (idx < 0) return; // 已被 popstate 消费过，或从未接管 —— 两种情况都不该再动 history

    // 非栈顶（浮层乱序关闭）：只从栈里摘掉，不动 history。
    // 中间那条历史条目没法单独删，留着；将来它浮上来时 onPopState 会因 token 不在栈里而
    // 静默消费掉（表现为一次「无效返回」）。严格 LIFO 的常规用法不会走到这里。
    if (idx !== stack.length - 1) {
        stack.splice(idx, 1);
        return;
    }

    const entry = stack.pop();

    // 已有一条在飞就不叠加：两条 back() 会退掉真页面
    if (pendingPop) return;

    // href 必须取**压栈时**那一个，不能取此刻的 window.location.href ——
    // 「在浮层里点一项然后导航走」时两者并不相等：Blazor 在事件分发期间推迟渲染
    // （_isBatchInProgress），于是 NavigateTo 的 pushState 先到，浮层关闭引发的 pop()
    // 后到。取此刻的 href 就等于说「我还站在占位条目上」，flushPop 照 back() 不误，
    // 把用户从刚打开的新页退回旧页 —— 表现是「点了打不开」。
    pendingPop = { token, href: entry.href };
    // 拖一个宏任务，给同批渲染里的 push() 让路（理由见 pendingPop 的注释）
    setTimeout(flushPop, 0);
}

/**
 * 「这一层马上要被一次真导航顶掉」——把栈顶条目摘掉但**不动 history**，
 * 返回 true 表示调用方应当用 replace 语义导航。
 *
 * 为什么需要它：占位条目与它下面那条真条目是同一个 URL。若导航直接 pushState 压上去，
 * 历史是 [真A, 占位A, B]，从 B 按返回先落到「占位A」（渲染出 A），再按一次又是 A ——
 * 中间那一下就是用户眼里「按了没反应」。改用 replace，占位条目正好被新页顶掉：[真A, B]。
 *
 * 返回 false 的情形（未开启 / 非手机 / 栈空 / 已经不站在栈顶那条上）调用方按普通 push 导航。
 */
function consumeTop() {
    if (!enabled || stack.length === 0) return false;

    const top = stack[stack.length - 1];
    if (top.href !== window.location.href) return false;

    stack.pop();
    // 同一层若已排了一次 back()，一并取消 —— 那条占位条目这就要被 replace 顶掉了
    if (pendingPop && pendingPop.token === top.token) pendingPop = null;
    return true;
}

/** 把待弹的占位条目真正弹掉。这中间若已发生真导航，就作废 —— 否则会把用户退回上一页。 */
function flushPop() {
    if (!pendingPop) return;

    const { token, href } = pendingPop;
    pendingPop = null;

    if (window.location.href !== href) return;

    selfPopToken = token;
    try {
        history.back();
    } catch {
        selfPopToken = null;
    }
}

/** 宿主显式开启（Admin 在 Routes.razor 首帧后调用一次）。POS 不调 = 完全不受影响。 */
function enable() {
    enabled = true;
    bind();
}

function disable() {
    enabled = false;
    stack.length = 0;
    selfPopToken = null;
    pendingPop = null;
}

/** 当前接管的层数，调试与测试用 */
function depth() {
    return stack.length;
}

window.ArkUI = window.ArkUI || {};
window.ArkUI.BackStack = { enable, disable, push, pop, consumeTop, depth };
